import React from "react";

import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { ConnectionStatistics } from "../../../../models/userConnections";
import { MODES } from "../ConnectionStatistic";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CHART_BG_COLORS, CHART_BORDER_COLORS } from "../../../../constants/colors";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

export interface IConnectionTableProps  {
    connections : ConnectionStatistics[];
    loading: boolean;
    mode: MODES;
}

const ConnectionTable = ({connections, loading, mode} : IConnectionTableProps) => {

    const prepareChartData = () => {
        if (!connections || connections.length === 0) return null;
        
        const labels = connections.map(item => item.appName);
        const data = connections.map(item => item.count);
        
        const bgColors = labels.map((_, i) => CHART_BG_COLORS[i % CHART_BG_COLORS.length]);
        const bdColors = labels.map((_, i) => CHART_BORDER_COLORS[i % CHART_BORDER_COLORS.length]);
        
        return {
            labels,
            datasets: [
                {
                    label: 'Nombre de connexions',
                    data,
                    backgroundColor: bgColors,
                    borderColor: bdColors,
                    borderWidth: 1,
                },
            ],
        };
    };
    
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },  
            tooltip: {
                enabled: true,
            },
            datalabels: {
                color: '#000',
                anchor: 'center' as const,
                align: 'center' as const,
                formatter: (value: number) => {
                    return value > 0 ? value.toString() : '';
                },
                font: {
                    weight: 'bold' as const
                }
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                }
            }
        }
    };
    
    const chartData = prepareChartData();
    
    if (mode === MODES.Table) {
        return <TableContainer>
    <Table>

    <TableHead>
        <TableRow style={{ backgroundColor: '#f5f5f5'}}>
            <TableCell colSpan={2} align="center">Applications</TableCell>
        </TableRow>
        <TableRow>
            <TableCell>Application</TableCell>
            <TableCell>Nombre de connexions</TableCell>
        </TableRow>
    </TableHead>
    <TableBody>	
        {loading ? (
            <TableRow>
                <TableCell colSpan={2} style={{ textAlign: 'center' }}>
                    <CircularProgress />
                </TableCell>
            </TableRow>
        ) : connections?.length ? (
            connections.map((statistic: ConnectionStatistics) => (
                <TableRow key={statistic.id}>
                    <TableCell>{statistic.appName}</TableCell>
                    <TableCell>{statistic.count}</TableCell>
                </TableRow>
            ))
            ) : (
                <TableRow>
                    <TableCell colSpan={2} style={{ textAlign: 'center' }}>
                        Aucune donnée	
                    </TableCell>
                </TableRow>
            )}
        </TableBody>
    </Table>
</TableContainer>
    } else if (mode === MODES.Chart) {
        return (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ width: '100%', textAlign: 'center', mb: 2 }}>
                    <h3>Nombre de connexions par application</h3>
                </Box>
                <Box sx={{ height: 400, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {loading ? (
                        <CircularProgress />
                    ) : connections?.length ? (
                        <Box sx={{ height: '100%', width: '100%', maxWidth: 800, position: 'relative' }}>
                            {chartData && <Bar data={chartData} options={chartOptions} />}
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center' }}>Aucune donnée</Box>
                    )}
                </Box>
            </Box>
        );
    }
    
    return null;
}

export default ConnectionTable;